import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, History, Loader2, SlidersHorizontal, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const REASONS = ["Sayım farkı", "Hasarlı ürün", "Kayıp", "Fazla stok", "Hatalı giriş düzeltmesi", "Diğer"];
const KIND_LABEL = {
  purchase: "Mal Kabul", sale: "Satış/Sevk", shipment: "Sevkiyat", transfer_out: "Transfer Çıkış",
  transfer_in: "Transfer Giriş", sayim: "Stok Sayımı", production: "Üretim", fire: "Fire", return: "İade",
  sample_out: "Numune Çıkış", sample_return: "Numune İade", sample_to_sale: "Numune → Satış", sample_writeoff: "Numune Zayi",
};
const kindLabel = (m) => (m.kind === "manuel" ? (m.reference_type === "stock_adjustment" ? "Stok Düzeltme" : "Manuel Hareket") : KIND_LABEL[m.kind] || m.kind || "—");
const INP = "bg-slate-950 border-slate-800 rounded-sm h-9 text-sm";

function Header({ cell }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border border-slate-800 rounded-sm p-2.5 bg-slate-950/50" data-testid="cell-op-header">
      <span className="text-slate-500">Ürün</span><span className="text-slate-100 text-right truncate">{cell.product_name}</span>
      <span className="text-slate-500">Beden</span><span className="text-slate-100 text-right">{cell.label}</span>
      <span className="text-slate-500">Depo</span><span className="text-slate-100 text-right">{cell.warehouse_name}</span>
      <span className="text-slate-500">Fiziki</span><span className="font-mono text-right text-slate-100" data-testid="cell-op-physical">{fmt(cell.physical)}</span>
      <span className="text-slate-500">Kullanılabilir</span><span className="font-mono text-right text-emerald-400" data-testid="cell-op-available">{fmt(cell.available)}</span>
    </div>
  );
}

function AdjustForm({ cell, onDone }) {
  // MUTLAK değer: kullanıcı yeni fiziki stoğu girer; fark sunucuda (hedef − mevcut) hesaplanır
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const reqId = useMemo(uid, []);
  const t = Number(String(target).replace(",", "."));
  const delta = Number.isFinite(t) ? Math.round((t - cell.physical) * 1e6) / 1e6 : 0;
  const invalid = target === "" || !Number.isFinite(t) || t < 0 || delta === 0 || !reason || (reason === "Diğer" && !desc.trim());
  const submit = async () => {
    if (invalid || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/stock/movement", {
        product_id: cell.product_id, variant_id: cell.variant_id || "", warehouse_id: cell.warehouse_id,
        target_quantity: t, quantity_delta: delta, kind: "manuel", reference_type: "stock_adjustment", client_request_id: reqId,
        reason: desc.trim() ? `${reason} — ${desc.trim()}` : reason,
      });
      const d = Number(data?.quantity_delta || 0);
      toast.success(`Stok düzeltildi: ${fmt(data?.quantity_before)} → ${fmt(data?.quantity_after)} (${d >= 0 ? "+" : ""}${fmt(d)})`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="stock-adjust-form">
      <div>
        <label className="text-[11px] text-slate-400">Yeni fiziki stok miktarı *</label>
        <Input autoFocus inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={`Mevcut: ${fmt(cell.physical)}`} className={INP} data-testid="stock-adjust-target" />
        {target !== "" && Number.isFinite(t) && (
          <div className={`text-[11px] font-mono mt-1 ${t < 0 ? "text-rose-400" : "text-slate-400"}`} data-testid="stock-adjust-preview">
            {fmt(cell.physical)} → {fmt(t)} ({delta >= 0 ? "+" : ""}{fmt(delta)}){t < 0 && " (negatif stok olamaz)"}{delta === 0 && t >= 0 && " — değişiklik yok"}
          </div>
        )}
      </div>
      <div>
        <label className="text-[11px] text-slate-400">Düzeltme nedeni *</label>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className={INP} data-testid="stock-adjust-reason"><SelectValue placeholder="Neden seçin" /></SelectTrigger>
          <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[11px] text-slate-400">Açıklama {reason === "Diğer" ? "*" : "(opsiyonel)"}</label>
        <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-slate-950 border-slate-800 rounded-sm text-sm" data-testid="stock-adjust-desc" />
      </div>
      <Button onClick={submit} disabled={invalid || busy} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="stock-adjust-submit">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Düzeltmeyi Kaydet
      </Button>
    </div>
  );
}

function TransferForm({ cell, warehouses, onDone }) {
  const targets = warehouses.filter((w) => w.id !== cell.warehouse_id);
  const [tgt, setTgt] = useState(targets.length === 1 ? targets[0].id : "");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const transferId = useMemo(uid, []);
  const q = Number(String(qty).replace(",", "."));
  const over = Number.isFinite(q) && q > cell.available;
  const invalid = !tgt || !qty || !Number.isFinite(q) || q <= 0 || over;
  const submit = async () => {
    if (invalid || busy) return;
    setBusy(true);
    try {
      await api.post("/warehouses/transfers", {
        transfer_id: transferId, source_warehouse_id: cell.warehouse_id, target_warehouse_id: tgt, note: note.trim(),
        items: [{ product_id: cell.product_id, variant_id: cell.variant_id || "", quantity: q }],
      });
      toast.success(`${fmt(q)} adet transfer edildi`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="stock-transfer-form">
      <div>
        <label className="text-[11px] text-slate-400">Kaynak → Hedef depo *</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 h-9 px-3 flex items-center text-sm border border-slate-800 rounded-sm bg-slate-950/60 text-slate-300 truncate">{cell.warehouse_name}</div>
          <ArrowLeftRight className="w-4 h-4 text-slate-500 shrink-0" />
          <Select value={tgt} onValueChange={setTgt}>
            <SelectTrigger className={`${INP} flex-1 min-w-0`} data-testid="stock-transfer-target"><SelectValue placeholder="Hedef depo" /></SelectTrigger>
            <SelectContent>{targets.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-[11px] text-slate-400">Miktar * (en fazla {fmt(cell.available)} — rezerve/opsiyon hariç)</label>
        <Input autoFocus inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className={INP} data-testid="stock-transfer-qty" />
        {over && <div className="text-[11px] text-rose-400 mt-1" data-testid="stock-transfer-over">Kullanılabilir miktarı aşıyor</div>}
      </div>
      <div>
        <label className="text-[11px] text-slate-400">Not (opsiyonel)</label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} className={INP} data-testid="stock-transfer-note" />
      </div>
      <Button onClick={submit} disabled={invalid || busy} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="stock-transfer-submit">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Transfer Et
      </Button>
    </div>
  );
}

function MovementList({ cell }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/products/${cell.product_id}/stock-movements`, { params: { variant_id: cell.variant_id || "", warehouse_id: cell.warehouse_id, limit: 200 } })
      .then(({ data }) => setRows(data || [])).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [cell]);
  if (rows === null) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>;
  if (!rows.length) return <div className="py-6 text-center text-sm text-slate-500" data-testid="stock-movements-empty">Hareket bulunamadı.</div>;
  return (
    <div className="space-y-1.5 max-h-[55vh] overflow-y-auto" data-testid="stock-movements-list">
      {rows.map((m) => {
        const dq = Number(m.quantity_delta || 0);
        return (
          <div key={m.id} className="border border-slate-800 rounded-sm px-2.5 py-2 text-xs" data-testid="stock-movement-row">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-100">{kindLabel(m)}</span>
              <span className={`font-mono font-semibold ${dq >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{dq >= 0 ? "+" : ""}{fmt(dq)}</span>
              {m.quantity_before != null && <span className="font-mono text-slate-400">{fmt(m.quantity_before)} → {fmt(m.quantity_after)}</span>}
              <span className="ml-auto text-[10px] text-slate-500 whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
              {m.counterpart_warehouse_name && <span>{dq < 0 ? "→" : "←"} {m.counterpart_warehouse_name}</span>}
              {m.reference_id && m.reference_type !== "stock_adjustment" && <span className="font-mono">Ref: {String(m.reference_id).slice(0, 12)}</span>}
              {m.reason && <span className="truncate">{m.reason}</span>}
              <span className="text-slate-500">{m.created_by || "—"}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Depo stok hücresi işlem penceresi: menü → Stok Düzelt / Transfer Et / Hareketleri Gör
export function StockCellActions({ cell, warehouses, canEdit, onClose, onChanged }) {
  const [mode, setMode] = useState("menu");
  useEffect(() => { setMode("menu"); }, [cell]);
  const done = () => { onChanged(); onClose(); };
  const titles = { menu: "Stok İşlemi", adjust: "Stok Düzelt", transfer: "Transfer Et", moves: "Stok Hareketleri" };
  const opt = "w-full justify-start rounded-sm h-11 border-slate-800 bg-slate-950/50 hover:bg-slate-800";
  return (
    <Dialog open={!!cell} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-md bg-slate-900 border-slate-800 p-4 gap-3" data-testid="stock-cell-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            {mode !== "menu" && <button type="button" onClick={() => setMode("menu")} className="text-slate-400 hover:text-slate-100" data-testid="stock-cell-back"><ArrowLeft className="w-4 h-4" /></button>}
            {titles[mode]}
          </DialogTitle>
        </DialogHeader>
        {cell && <Header cell={cell} />}
        {cell && mode === "menu" && (
          <div className="grid gap-2">
            {canEdit && <Button variant="outline" className={opt} onClick={() => setMode("adjust")} data-testid="stock-cell-adjust"><SlidersHorizontal className="w-4 h-4 mr-2 text-blue-400" /> Stok Düzelt</Button>}
            {canEdit && warehouses.length > 1 && <Button variant="outline" className={opt} onClick={() => setMode("transfer")} data-testid="stock-cell-transfer"><ArrowLeftRight className="w-4 h-4 mr-2 text-amber-400" /> Transfer Et</Button>}
            <Button variant="outline" className={opt} onClick={() => setMode("moves")} data-testid="stock-cell-movements"><History className="w-4 h-4 mr-2 text-slate-300" /> Hareketleri Gör</Button>
          </div>
        )}
        {cell && mode === "adjust" && <AdjustForm cell={cell} onDone={done} />}
        {cell && mode === "transfer" && <TransferForm cell={cell} warehouses={warehouses} onDone={done} />}
        {cell && mode === "moves" && <MovementList cell={cell} />}
      </DialogContent>
    </Dialog>
  );
}
